/**
 * Field constraint definition extracted from OpenAPI spec.
 * These are used to normalize data before validation.
 */
export interface FieldConstraints {
  /** Maximum string length */
  maxLength?: number;
  /** Minimum string length */
  minLength?: number;
  /** Maximum array items */
  maxItems?: number;
  /** Minimum array items */
  minItems?: number;
  /** Array items must be unique */
  uniqueItems?: boolean;
  /** Field is nullable */
  nullable?: boolean;
  /** Nested object constraints */
  properties?: Record<string, FieldConstraints>;
  /** Array item constraints */
  items?: FieldConstraints;
}

export interface NormalizationConfig {
  /** Log warnings when auto-fixing. Defaults to true in development. */
  logWarnings?: boolean;
}

interface AutoFixResult {
  value: unknown;
  fixes: string[];
}

/**
 * Normalize data to handle quasi-equivalent values.
 *
 * This only performs "safe" normalizations where values are semantically equivalent:
 * - Empty array [] → null (when nullable with minItems > 0)
 *
 * It does NOT silently truncate or drop data - that would cause data loss.
 * Invalid values (too long strings, too many items) should fail server validation.
 */
function autoFix(data: unknown, constraints: FieldConstraints, path: string[]): AutoFixResult {
  const fixes: string[] = [];
  const pathStr = path.join('.') || 'root';

  // Handle null/undefined
  if (data === null || data === undefined) {
    return { value: data, fixes };
  }

  // Handle arrays
  if (Array.isArray(data)) {
    let result = [...data];

    // Empty array with minItems constraint → convert to null (if nullable)
    // This is a quasi-equivalent transformation: [] and null mean the same thing
    // for nullable arrays in Go backends
    if (result.length === 0 && constraints.minItems && constraints.minItems > 0) {
      if (constraints.nullable) {
        fixes.push(`${pathStr}: Converted empty array to null (minItems: ${constraints.minItems})`);
        return { value: null, fixes };
      }
    }

    // Recursively normalize array items (for nested quasi-equivalents)
    if (constraints.items) {
      result = result.map((item, idx) => {
        const { value, fixes: itemFixes } = autoFix(item, constraints.items!, [
          ...path,
          `[${idx}]`,
        ]);
        fixes.push(...itemFixes);
        return value;
      });
    }

    return { value: result, fixes };
  }

  // Handle objects - recursively process nested fields
  if (typeof data === 'object' && constraints.properties) {
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      if (key in constraints.properties) {
        const { value: normalizedValue, fixes: fieldFixes } = autoFix(
          value,
          constraints.properties[key],
          [...path, key],
        );
        fixes.push(...fieldFixes);
        result[key] = normalizedValue;
      } else {
        // Preserve unknown fields
        result[key] = value;
      }
    }

    return { value: result, fixes };
  }

  // For all other types (strings, numbers, etc.), return as-is
  // We don't truncate strings or drop array items - that would be data loss
  return { value: data, fixes };
}

/**
 * Normalize data without schema validation.
 * Useful for testing or when schema validation is handled separately.
 */
export function normalizeData(
  data: unknown,
  constraints: FieldConstraints,
  config: NormalizationConfig = {},
): { value: unknown; fixes: string[] } {
  const { logWarnings = process.env.NODE_ENV !== 'production' } = config;
  const result = autoFix(data, constraints, []);

  if (logWarnings && result.fixes.length > 0) {
    console.warn('[API Normalization] Auto-fixed issues:', result.fixes);
  }

  return result;
}

/**
 * Simple inline normalizer for use in API repos.
 * Returns the normalized data with proper typing.
 *
 * @example
 * ```typescript
 * async createLineItem(data: CreateLineItemRequest) {
 *   const res = await this.api.createLineItem(normalized(data, lineItemRequestConstraints));
 *   return res.data.data;
 * }
 * ```
 */
export function normalized<T>(data: T, constraints: FieldConstraints): T {
  const { value } = normalizeData(data, constraints);
  return value as T;
}
